/**
 * CompactScoreTile — Mixpanel-inspired 3-line score card.
 *
 * Fork Tier-A-#1 (A.3). Inspired by nicelydone Mixpanel `a9d9b632`
 * ("Compact bar/score tile"), re-skinned in Butler palette
 * (`midway-500` fill on `hairline-soft` track).
 *
 * Intentionally dumb — the parent page computes the score from an
 * appropriate backend shape and hands this tile just the percentage +
 * caption. That way the same component renders "Kiosk Health" from
 * /extras.kiosk_throughput and "Plan Mix Diversity" from /extras.plan_mix
 * without any knowledge of the source schema.
 *
 * Graceful empty-state: pass `scorePct: null` + `unavailableMessage` to
 * render a subdued placeholder tile instead of blanking the layout.
 * Loading state shown as a hairline-pulse skeleton that matches the tile's
 * real footprint so the page doesn't reflow when data arrives.
 */

interface CompactScoreTileProps {
  /** Short upper-case label, e.g. "Kiosk Health". */
  label: string
  /**
   * 0-100 percentage. `null` flips the tile into its empty-state shell.
   * Values are clamped to [0, 100] before rendering so upstream math
   * bugs don't overflow the bar.
   */
  scorePct: number | null
  /**
   * Short secondary caption ("Above avg", "3 pts above baseline"). Hidden
   * when the tile is in the empty state.
   */
  caption?: string
  /**
   * Copy shown in place of the score when `scorePct === null`. Keep it
   * short; this is meant to be read at a glance.
   */
  unavailableMessage?: string
  /** Skeleton-while-fetching. Wins over the empty state if both apply. */
  loading?: boolean
  'data-testid'?: string
}

function clampPct(n: number): number {
  if (!Number.isFinite(n)) return 0
  if (n < 0) return 0
  if (n > 100) return 100
  return Math.round(n)
}

export function CompactScoreTile({
  label,
  scorePct,
  caption,
  unavailableMessage = 'Data unavailable',
  loading = false,
  'data-testid': testId,
}: CompactScoreTileProps) {
  const resolvedTestId = testId ?? `score-tile-${label.toLowerCase().replace(/\s+/g, '-')}`

  if (loading) {
    return (
      <div
        data-testid={resolvedTestId}
        data-state="loading"
        aria-busy="true"
        aria-label={`Loading ${label}`}
        className="rounded-card border border-hairline bg-white p-4 shadow-card-soft dark:border-slate-700 dark:bg-slate-800"
      >
        <div className="h-3 w-24 animate-pulse rounded bg-hairline dark:bg-slate-700" />
        <div className="mt-3 h-7 w-16 animate-pulse rounded bg-hairline dark:bg-slate-700" />
        <div className="mt-3 h-1 w-full animate-pulse rounded bg-hairline dark:bg-slate-700" />
        <div className="mt-2 h-3 w-20 animate-pulse rounded bg-hairline dark:bg-slate-700" />
      </div>
    )
  }

  if (scorePct === null) {
    return (
      <div
        data-testid={resolvedTestId}
        data-state="empty"
        className="rounded-card border border-dashed border-hairline bg-surface-stripe p-4 shadow-card-soft dark:border-slate-700 dark:bg-slate-800/60"
      >
        <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-ink-soft dark:text-slate-400">
          {label}
        </div>
        <div className="mt-2 font-display text-lg font-semibold text-ink-soft dark:text-slate-400">
          —
        </div>
        <div className="mt-3 h-1 w-full rounded bg-hairline-soft dark:bg-slate-700" />
        <div className="mt-2 text-xs text-ink-soft dark:text-slate-400">
          {unavailableMessage}
        </div>
      </div>
    )
  }

  const pct = clampPct(scorePct)

  return (
    <div
      data-testid={resolvedTestId}
      data-state="ready"
      data-score={pct}
      className="rounded-card border border-hairline bg-white p-4 shadow-card-soft transition hover:shadow-card dark:border-slate-700 dark:bg-slate-800"
    >
      <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-ink-soft dark:text-slate-400">
        {label}
      </div>
      <div className="mt-2 font-display text-2xl font-bold text-butler-700 dark:text-butler-100">
        {pct}%
      </div>
      <div
        role="progressbar"
        aria-valuenow={pct}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={`${label} ${pct}%`}
        className="mt-3 h-1 w-full overflow-hidden rounded bg-hairline-soft dark:bg-slate-700"
      >
        <div
          data-testid={`${resolvedTestId}-fill`}
          className="h-full rounded bg-midway-500 transition-[width] duration-300"
          style={{ width: `${pct}%` }}
        />
      </div>
      {caption ? (
        <div className="mt-2 text-xs text-ink-soft dark:text-slate-400">{caption}</div>
      ) : null}
    </div>
  )
}
